/**
 * @description Component-PriceInput
 */
import './index.less';

import Alert from '@/components/Alert';
import { DEFAULT_ORDER_COUNT, DEFAULT_STEP_RATIO } from '@/constants/trade';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { WrappedPair } from '@/entities/WrappedPair';
import { useGetDefaultLimitPrice, usePairLimitPriceRangeTicks } from '@/features/pair/hook';
import { useUpdatePriceByTicks } from '@/features/trade/hooks';
import { Side } from '@synfutures/sdks-perp';
import { useDebounceEffect, useDebounceFn } from 'ahooks';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PriceInputSlider from './PriceInputSlider';
import ScaleOrderCount from './ScaleOrderCount';

import { useTradeStore } from '@/features/trade/store';
import { Context } from '@derivation-tech/context';
interface IPropTypes {
  children?: React.ReactNode;
  className?: string;
  currentPair: WrappedPair | undefined;
  sdkContext: Context | undefined;
  tradeSide: Side;
  lowerPrice: WrappedBigNumber | undefined;
  upperPrice: WrappedBigNumber | undefined;
  disabled?: boolean;
}
const PriceInput: FC<IPropTypes> = function ({ currentPair, disabled, sdkContext, tradeSide, lowerPrice, upperPrice }) {
  // const dispatch = useAppDispatch();
  const {
    lowerTick,
    upperTick,
    orderCount,
    scaleLimitStepRatio,
    // alignedLowerPrice, alignedUpperPrice
  } = useTradeStore();
  const { t } = useTranslation();
  const [sliderFocused, setSlierFocused] = useState(false);
  const updatePricesByTicks = useUpdatePriceByTicks();
  const lowerUpperTicks = usePairLimitPriceRangeTicks(currentPair, tradeSide, sdkContext);
  // const changeLimitPriceWhenOutOfRange = useScaleLimitPriceChangeWhenOutOfRange(chainId, inputFocused);
  const warningMsg = useMemo(() => {
    if (upperTick !== 0 && lowerTick !== 0) {
      if (WrappedBigNumber.from(lowerTick).gte(upperTick)) {
        return t('common.tradePage.warningMsg.lowerMoreThanUpper');
      }
    }
  }, [lowerTick, upperTick, t]);
  // const showAlignInfo = useMemo(
  //   () =>
  //     !warningMsg &&
  //     lowerPrice?.lte(lowerPrice || 0) &&
  //     upperPrice?.gte(upperPrice || 0) &&
  //     lowerPrice &&
  //     upperPrice &&
  //     (WrappedBigNumber.from(alignedLowerPrice || 0)?.notEq(lowerPrice) ||
  //       WrappedBigNumber.from(alignedUpperPrice || 0)?.notEq(alignedUpperPrice)),
  //   [lowerPrice, alignedLowerPrice, alignedUpperPrice, upperPrice, warningMsg],
  // );
  const getDefaultLimitPriceTick = useGetDefaultLimitPrice(currentPair, tradeSide);
  const setDefaultLimitPriceTicks = useCallback(
    (tradeSide: Side) => {
      const defaultLimit = getDefaultLimitPriceTick(tradeSide);
      let countTicksGap = (DEFAULT_ORDER_COUNT - 1) * DEFAULT_STEP_RATIO;
      if (orderCount && scaleLimitStepRatio) {
        countTicksGap = scaleLimitStepRatio * (orderCount - 1);
      }
      if (defaultLimit && currentPair) {
        const defaultTick = defaultLimit.tick * (currentPair?.isInverse ? -1 : 1);
        if (tradeSide === Side.LONG) {
          const lowerInitialTick = defaultTick - countTicksGap;
          updatePricesByTicks(lowerInitialTick, defaultTick, currentPair);
        } else {
          const upperInitialTick = defaultTick + countTicksGap;
          updatePricesByTicks(defaultTick, upperInitialTick, currentPair);
        }
      }
    },
    [currentPair, getDefaultLimitPriceTick, orderCount, scaleLimitStepRatio, updatePricesByTicks],
  );
  const { run: onUpperLowerTicksChange } = useDebounceFn(() => {
    if (sliderFocused || !currentPair) return;
    const [lowerTick, upperTick] = lowerUpperTicks;
    const countTicksGap = (orderCount - 1) * scaleLimitStepRatio;
    if (lowerTick > lowerTick) {
      updatePricesByTicks(lowerTick, lowerTick + countTicksGap, currentPair);
    }
    if (upperTick < upperTick) {
      updatePricesByTicks(upperTick - countTicksGap, upperTick, currentPair);
    }
  });

  useEffect(() => {
    onUpperLowerTicksChange();
    // only this dep
  }, [lowerUpperTicks]);
  useDebounceEffect(() => {
    !lowerTick && setDefaultLimitPriceTicks(tradeSide);
  }, [lowerUpperTicks, lowerTick]);
  useDebounceEffect(() => {
    setDefaultLimitPriceTicks(tradeSide);
  }, [tradeSide, currentPair?.id]);
  return (
    <>
      {/* <TradeFormSide
        disabled={scaleLimitFormStatus === FETCHING_STATUS.FETCHING}
        chainId={chainId}
        onSideChange={(key) => {
          const tradeSide = Number(key) as Side;
          setDefaultLimitPriceTicks(tradeSide);
          chainId &&
            dispatch(
              setTradeFormSide({
                chainId,
                tradeSide,
              }),
            );
        }}
      /> */}
      <ScaleOrderCount
        lowerLimitTick={lowerUpperTicks[0]}
        upperLimitTick={lowerUpperTicks[1]}
        tradeSide={tradeSide}
        sdkContext={sdkContext}
        pair={currentPair}
      />
      <div className="syn-scale-limit-form-section-price-adjust">
        <div className="syn-scale-limit-form-section-title">
          <span>
            {t('common.priceR')}
            {/* ({lowerPrice?.formatLiqPriceNumberWithTooltip()} -{' '}
            {upperPrice?.formatLiqPriceNumberWithTooltip()}) */}
          </span>
        </div>
        <PriceInputSlider
          decimals={currentPair?.rootInstrument.quoteToken.decimals}
          disabled={disabled}
          range={lowerUpperTicks}
          tradeSide={tradeSide}
          upperPrice={upperPrice}
          lowerPrice={lowerPrice}
          setSlierFocused={setSlierFocused}
          onChange={(values) => currentPair && updatePricesByTicks(values[0], values[1], currentPair)}
        />
        {warningMsg && <Alert type="error" showIcon={true} message={warningMsg} />}
        {/* {showAlignInfo && (
          <Alert
            type="info"
            showIcon
            message={
              <>
                {t('common.tradePage.scaleLimitSub')}
                <Tooltip
                  overlayInnerStyle={{ width: 'fit-content' }}
                  title={`${alignedLowerPrice} ~ ${alignedUpperPrice}`}>
                  <b className="syn-scale-limit-form-section-prices-range">
                    {WrappedBigNumber.from(alignedLowerPrice).formatNumberWithTooltip({
                      showToolTip: false,
                    })}{' '}
                    ~{' '}
                    {WrappedBigNumber.from(alignedUpperPrice).formatNumberWithTooltip({
                      showToolTip: false,
                    })}
                  </b>
                </Tooltip>
              </>
            }
          />
        )} */}
      </div>
    </>
  );
};

export default PriceInput;
